import {
  FundingMeta,
  IProductMetaQueryRepository,
  LimitedMeta,
  LowStockMeta,
  LowStockVariantId,
  ProductId,
  ProductMeta,
  ProductMetaCode,
} from '../domain';
import { AllMpsPoolType, AllPoolType } from '@services/MarppleShop/shared/database/db.ts';
import { each, entries, groupBy, map, pipe, reduce } from '@fxts/core';
import { MetaCodeVariant } from '@services/MarppleShop/features/ProductMeta';

interface TableAliases {
  mps_product_meta_code_variants: string;
  meta_code_variants: string;
  meta_codes: string;
}

interface ProductMetaRow {
  product_id: number;
  variant_id: number;
  meta_code: ProductMetaCode;
  variant: string;

  // numeric 이라 string 으로 반환됩니다
  value_numeric: string | null;

  value_text: string | null;
  value_jsonb: object | null;
  created_at: Date;
  updated_at: Date;
}

export class ProductMetaQueryRepositoryFactory {
  private static readonly TABLE_ALIAS_MAP = new Map<string, TableAliases>([
    [
      'MCO',
      {
        meta_codes: 'meta_codes',
        meta_code_variants: 'meta_code_variants',
        mps_product_meta_code_variants: 'mps_product_meta_code_variants',
      },
    ],
    [
      'MPS',
      {
        meta_codes: 'meta_codes_view',
        meta_code_variants: 'meta_code_variants_view',
        mps_product_meta_code_variants: 'mps_product_meta_code_variants_view',
      },
    ],
  ]);

  /**
   * MCO DB를 사용합니다. (AllPoolType만 주입 가능)
   */
  static createMco(db: AllPoolType): IProductMetaQueryRepository {
    const table_alias = this.TABLE_ALIAS_MAP.get('MCO');
    if (!table_alias) {
      throw new Error('Table alias not found');
    }
    return new ProductMetaQueryRepository(db, table_alias);
  }

  /**
   * MPS DB를 사용합니다. (AllMpsPoolType만 주입 가능)
   */
  static createMps(db: AllMpsPoolType): IProductMetaQueryRepository {
    const table_alias = this.TABLE_ALIAS_MAP.get('MPS');
    if (!table_alias) {
      throw new Error('Table alias not found');
    }
    return new ProductMetaQueryRepository(db, table_alias);
  }
}

/**
 * @see {ProductMetaQueryRepositoryFactory}
 * - table 명이 다른데 조건절이 동일해서 alias 를 주입하도록 설계했습니다.
 * - 주입할 때 human error 를 방지하기 위해 factory로 말아두었어요.
 *
 * - 나중에 테이블 스키마가 달라지면 repoistory 함수를 분리해주세요.
 */
class ProductMetaQueryRepository implements IProductMetaQueryRepository {
  private readonly tables: TableAliases;

  constructor(
    private readonly db: AllPoolType | AllMpsPoolType,
    alias: TableAliases,
  ) {
    this.tables = {
      meta_codes: this.db.CL(`${alias.meta_codes}`),
      meta_code_variants: this.db.CL(`${alias.meta_code_variants}`),
      mps_product_meta_code_variants: this.db.CL(`${alias.mps_product_meta_code_variants}`),
    };
  }

  async findByProductId(productId: ProductId): Promise<ProductMeta | undefined> {
    const metaMap = await this.findByProductIds([productId]);

    const productMeta = metaMap.get(productId.value);

    return productMeta;
  }

  async findByProductIds(productIds: ProductId[]): Promise<Map<number, ProductMeta>> {
    const product_ids = productIds.map((productId) => productId.value);

    const rows: ProductMetaRow[] = await this.db.QUERY<ProductMetaRow>`
      SELECT pcv.product_id, v.id AS variant_id, v.meta_code, v.variant, pcv.value_numeric, pcv.value_text, pcv.value_jsonb, pcv.created_at, pcv.updated_at
      FROM ${this.tables.mps_product_meta_code_variants} AS pcv
        INNER JOIN ${this.tables.meta_code_variants} AS v ON v.id = pcv.meta_code_variant_id
        INNER JOIN ${this.tables.meta_codes} AS c ON c.code = v.meta_code AND c.marketplace_id = v.meta_code_marketplace_id
      WHERE TRUE
        AND ${this.db.IN('pcv.product_id', product_ids)}
        AND c.status = 'ACTIVE'
        AND v.status = 'ACTIVE'
    `;

    const metaHashMap: Map<number, ProductMeta> = new Map();

    pipe(
      rows,
      groupBy((row) => row.product_id.toString()),
      entries,
      each(([productId, metaRows]) => {
        const productIdVO = new ProductId(parseInt(productId));

        const variants: MetaCodeVariant[] = metaRows.map((metaRow) => {
          return MetaCodeVariant.from({
            id: metaRow.variant_id,
            variant: metaRow.variant,
            code: metaRow.meta_code,
            createdAt: metaRow.created_at,
          });
        });

        // TODO: @grape
        // TODO: @kjj 이 아래 코드 MetaCodeVariant 를 활용하도록 수정하기
        const limitedVariant = variants.find((v) => v.code === ProductMetaCode.LIMITED);
        const lowStockVariant = variants.find((v) => v.code === ProductMetaCode.LOW_STOCK);
        const fundingVariant = metaRows.find((row) => row.meta_code === ProductMetaCode.FUNDING);

        const productMeta = ProductMeta.from({
          productId: productIdVO,
          limited_meta: new LimitedMeta(!!limitedVariant?.id),
          low_stock_threshold_meta: lowStockVariant
            ? LowStockMeta.create(lowStockVariant.variant as LowStockVariantId)
            : undefined,
          funding_meta:
            fundingVariant && fundingVariant.value_numeric
              ? FundingMeta.create({
                  target_amount: parseInt(fundingVariant.value_numeric),
                  ...(fundingVariant.value_text ? { description: fundingVariant.value_text } : {}),
                })
              : undefined,
        });

        metaHashMap.set(productIdVO.value, productMeta);
      }),
    );

    return metaHashMap;
  }

  async findMetaCodeVariants(
    items: {
      meta_code: ProductMetaCode;
      variant: string;

      // TODO: 나중에 marketplace name 으로 받아야 함
      marketplace_id: number;
    }[],
  ): Promise<MetaCodeVariant[]> {
    // IN 안에 들어갈 쿼리
    const inValuesSql = pipe(
      items,
      map((item) => {
        return this.db.SQL`(${item.meta_code}, ${item.variant}, ${item.marketplace_id})`;
      }),
      reduce((a, b) => this.db.SQL`${a}, ${b}`),
    );

    const rows = await this.db.QUERY<{
      id: number;
      meta_code: ProductMetaCode;
      variant: string;
      marketplace_id: number;
      created_at: Date;
    }>`
      SELECT v.id, v.meta_code, v.variant, v.marketplace_id, v.created_at
      FROM ${this.tables.meta_code_variants} AS v
         INNER JOIN ${this.tables.meta_codes} AS c ON c.code = v.meta_code AND c.marketplace_id = v.meta_code_marketplace_id
      WHERE (v.meta_code, v.variant, v.meta_code_marketplace_id) IN (${inValuesSql})
        AND v.status = 'ACTIVE'
        AND c.status = 'ACTIVE'
    `;

    const variants = rows.map((row) => {
      return MetaCodeVariant.from({
        id: row.id,
        code: row.meta_code,
        variant: row.variant,
        createdAt: row.created_at,
      });
    });

    return variants;
  }

  async exists(product_id: ProductId): Promise<boolean> {
    const rows = await this.db.QUERY<{ product_id: number }>`
      SELECT product_id
      FROM ${this.tables.mps_product_meta_code_variants}
      WHERE product_id = ${product_id}
      LIMIT 1
    `;

    return rows.length > 0;
  }
}
